/**
 * Notification Center Component
 * Displays real-time notifications with management options
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Bell,
  Settings,
  Check,
  Trash2,
  Mail,
  AlertCircle,
  Clock,
  User,
  ChevronDown,
  AlertTriangle
} from 'lucide-react';
import useNotifications from '../hooks/useNotifications';
import NotificationPreferences from './NotificationPreferences';

const NotificationCenter = ({ token, user }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testSent, setTestSent] = useState(false);
  const dropdownRef = useRef(null);

  // Always call hooks unconditionally - this is critical for React's Rules of Hooks
  const {
    notifications,
    isConnected,
    unreadCount,
    markAsRead,
    clearAll,
    sendTestNotification,
    requestNotificationPermission,
    error
  } = useNotifications(token);

  // Safety check: Only render if we have a valid token and user
  if (!token || !user) {
    return null;
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Format notification timestamp
  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const notificationTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - notificationTime) / 60000);

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  // Get notification icon
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'lead':
      case 'service_request':
        return <User className="w-5 h-5 text-blue-500" />;
      case 'status_update':
        return <Clock className="w-5 h-5 text-gray-500" />;
      case 'alert':
      case 'urgent':
        return <AlertCircle className="w-5 h-5 text-orange-500" />;
      case 'email':
        return <Mail className="w-5 h-5 text-[#1b3a57]" />;
      default:
        return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }

    // Handle notification action if any
    if (notification.action_url) {
      window.location.href = notification.action_url;
    }
  };

  // Render preferences as modal overlay instead of inline replacement
  const renderPreferencesModal = () => {
    if (!showPreferences) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
          <NotificationPreferences
            token={token}
            onClose={() => setShowPreferences(false)}
          />
        </div>
      </div>
    );
  };

  // Show error state if there's a persistent error
  if (error) {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100"
          aria-label="Notifications (Error)"
          title={`Notifications Error: ${error}`}
        >
          <Bell className="w-6 h-6 text-gray-400" />
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-orange-400 rounded-full" title="Error" />
        </button>

        {/* Error Dropdown */}
        {showDropdown && (
          <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border z-50">
            <div className="p-4 border-b flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                <h3 className="font-semibold text-gray-900">Notifications Error</h3>
              </div>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600 mb-3">{error}</p>
              <button
                onClick={() => {
                  setShowDropdown(false);
                  window.location.reload(); // Simple retry by reloading
                }}
                className="text-sm bg-orange-500 text-white px-3 py-1 rounded hover:bg-orange-600 transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Notification Bell Button */}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100"
        aria-label="Notifications"
      >
        <Bell className="w-6 h-6" />

        {/* Connection Status Indicator */}
        <div className={`absolute -top-1 -left-1 w-3 h-3 rounded-full ${
          isConnected ? 'bg-gray-400' : error ? 'bg-orange-400' : 'bg-gray-300'
        }`} title={
          isConnected ? 'Connected' : error ? `Error: ${error}` : 'Disconnected'
        } />

        {/* Unread Count Badge */}
        {unreadCount > 0 && (
          <div className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center font-medium">
            {unreadCount > 99 ? '99+' : unreadCount}
          </div>
        )}
      </button>

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border z-50 max-h-96 flex flex-col">
          {/* Header */}
          <div className="p-4 border-b flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">Notifications</h3>
              <p className="text-sm text-gray-500">
                {isConnected ? '🟢 Live' : error ? '🔴 Error' : '🔴 Offline'} • {notifications.length} total
              </p>
              {error && (
                <p className="text-xs text-orange-500 mt-1">{error}</p>
              )}
            </div>

            <div className="flex items-center space-x-2">
              {/* Settings Button */}
              <button
                onClick={() => {
                  setShowPreferences(true);
                  setShowDropdown(false);
                }}
                className="p-1.5 text-gray-500 hover:text-gray-700 transition-colors rounded"
                title="Notification Settings"
              >
                <Settings className="w-4 h-4" />
              </button>

              {/* Clear All Button */}
              {notifications.length > 0 && (
                <button
                  onClick={clearAll}
                  className="p-1.5 text-gray-500 hover:text-orange-600 transition-colors rounded"
                  title="Clear All"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Notifications List */}
          <div className="flex-1 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 mb-2">No notifications yet</p>
                <button
                  onClick={async () => {
                    setSendingTest(true);
                    setTestSent(false);
                    const result = await sendTestNotification();
                    setSendingTest(false);
                    if (result) {
                      setTestSent(true);
                      setTimeout(() => setTestSent(false), 3000);
                    }
                  }}
                  disabled={sendingTest}
                  className="text-sm text-orange-500 hover:text-orange-600 transition-colors disabled:opacity-50"
                >
                  {sendingTest ? 'Sending...' : testSent ? '✓ Sent!' : 'Send Test Notification'}
                </button>
                {testSent && (
                  <p className="text-xs text-green-600 mt-2">
                    Test notification sent! (Note: WebSocket may not be connected)
                  </p>
                )}
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                      !notification.read ? 'bg-orange-50 border-l-4 border-orange-400' : ''
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      {/* Icon */}
                      <div className="flex-shrink-0">
                        {getNotificationIcon(notification.type)}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium text-gray-900 ${
                          !notification.read ? 'font-semibold' : ''
                        }`}>
                          {notification.title}
                        </p>
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-400 mt-2">
                          {formatTimestamp(notification.timestamp)}
                        </p>
                      </div>

                      {/* Read Status */}
                      {!notification.read && (
                        <div className="flex-shrink-0">
                          <div className="w-2 h-2 bg-orange-500 rounded-full" />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-3 border-t bg-gray-50 flex justify-between items-center text-sm">
              <span className="text-gray-500">
                {unreadCount} unread
              </span>
              <div className="flex items-center space-x-3">
                {/* Request Permission Button */}
                <button
                  onClick={requestNotificationPermission}
                  className="text-orange-500 hover:text-orange-600 transition-colors"
                  title="Enable Browser Notifications"
                >
                  Enable Notifications
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Preferences Modal Overlay */}
      {renderPreferencesModal()}
    </div>
  );
};

export default NotificationCenter;
